import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  buildKnowledgeSourceFormData,
  DEFAULT_KNOWLEDGE_LIMITS,
  faqTextToItems,
  formatKnowledgeBytes,
  validateKnowledgeSourceDraft,
} from "../../helpers/knowledgeManagement";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 18 },
  fields: { display: "flex", flexDirection: "column", gap: theme.spacing(2), minWidth: 480 },
  upload: { padding: theme.spacing(2), border: `1px dashed ${theme.palette.divider}`, borderRadius: 12 },
}));

const initial = { type: "pdf", name: "", text: "", url: "", faqText: "", file: null };
const publicPageErrors = {
  ERR_KNOWLEDGE_PUBLIC_URL_INVALID: "Informe uma URL HTTPS pública válida, sem porta ou credenciais.",
  ERR_KNOWLEDGE_PUBLIC_URL_BLOCKED: "A página aponta para um endereço de rede interna ou reservado.",
  ERR_KNOWLEDGE_PUBLIC_PAGE_UNAVAILABLE: "Não foi possível acessar a página pública. Confira o endereço e tente novamente.",
  ERR_KNOWLEDGE_PUBLIC_PAGE_TYPE_UNSUPPORTED: "A URL deve retornar uma página HTML pública.",
  ERR_KNOWLEDGE_PUBLIC_PAGE_LIMIT: "A página excede o tamanho permitido para captura.",
  ERR_KNOWLEDGE_PUBLIC_PAGE_EMPTY: "A página não contém texto útil para a base de conhecimento.",
  ERR_KNOWLEDGE_PUBLIC_PAGE_TIMEOUT: "A página demorou demais para responder. Tente novamente.",
  ERR_KNOWLEDGE_PUBLIC_PAGE_REDIRECT_LIMIT: "A página redirecionou vezes demais.",
  ERR_KNOWLEDGE_PUBLIC_PAGE_CHARSET_UNSUPPORTED: "A codificação de texto da página não é suportada.",
  ERR_KNOWLEDGE_TEXT_LIMIT: "A página contém mais texto que o limite permitido para uma fonte.",
};

const KnowledgeSourceModal = ({ open, onClose, onSaved, knowledgeBaseId, sourceId, sourceType, sourceUrl, sourceName, limits }) => {
  const classes = useStyles();
  const [form, setForm] = useState(initial);
  const [requestKey, setRequestKey] = useState(uuidv4());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...initial, type: sourceType || "pdf", url: sourceUrl || "", name: sourceName || "" });
    setRequestKey(uuidv4());
  }, [open, sourceId, sourceType, sourceUrl, sourceName]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const fileTypes = ["pdf", "txt", "markdown"];
  const effectiveLimits = { ...DEFAULT_KNOWLEDGE_LIMITS, ...(limits || {}) };
  const faqValid = form.type !== "faq" || faqTextToItems(form.faqText).every((item) => item.question && item.answer);
  const limitError = validateKnowledgeSourceDraft(form, effectiveLimits);
  const valid = form.name.trim().length >= 3
    && (!fileTypes.includes(form.type) || form.file)
    && (form.type !== "text" || form.text.trim())
    && (form.type !== "url" || form.url.trim())
    && (form.type !== "faq" || (form.faqText.trim() && faqValid))
    && !limitError;

  const save = async () => {
    setSaving(true);
    try {
      const data = buildKnowledgeSourceFormData(form);
      const url = sourceId
        ? `/knowledge-bases/${knowledgeBaseId}/sources/${sourceId}/versions`
        : `/knowledge-bases/${knowledgeBaseId}/sources`;
      const response = await api.post(url, data, {
        headers: { "Idempotency-Key": requestKey },
      });
      toast.success("Fonte recebida e enviada para processamento.");
      onSaved(response.data);
    } catch (error) {
      const code = error.response?.data?.error;
      if (form.type === "url" && publicPageErrors[code]) toast.error(publicPageErrors[code]);
      else toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const accept = form.type === "pdf" ? ".pdf,application/pdf" : form.type === "txt" ? ".txt,text/plain" : ".md,text/markdown,text/plain";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
      <DialogTitle>{sourceId ? (form.type === "url" ? "Atualizar página pública" : "Nova revisão da fonte") : "Adicionar fonte"}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.fields}>
          <Typography variant="body2" color="textSecondary">
            O envio retorna como processamento pendente. O conteúdo só poderá ser usado depois da extração e aprovação.
          </Typography>
          <FormControl variant="outlined">
            <InputLabel>Tipo</InputLabel>
            <Select value={form.type} onChange={(event) => setForm({ ...initial, type: event.target.value })} label="Tipo">
              <MenuItem value="pdf">PDF textual</MenuItem>
              <MenuItem value="txt">Arquivo TXT</MenuItem>
              <MenuItem value="markdown">Arquivo Markdown</MenuItem>
              <MenuItem value="text">Texto digitado</MenuItem>
              <MenuItem value="faq">Perguntas e respostas</MenuItem>
              <MenuItem value="url">Página pública (URL)</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Nome da fonte" variant="outlined" value={form.name} onChange={update("name")} required inputProps={{ maxLength: 255 }} />
          {form.type === "url" && (
            <TextField
              id="knowledge-public-page-url"
              label="URL da página pública"
              variant="outlined"
              type="url"
              value={form.url}
              onChange={update("url")}
              required
              inputProps={{ maxLength: 2048 }}
              placeholder="https://exemplo.com.br/ajuda"
              helperText={limitError || "Uma página HTML pública por fonte. O conteúdo será capturado agora e usado após aprovação; páginas com login não são suportadas."}
              error={Boolean(limitError)}
            />
          )}
          {fileTypes.includes(form.type) && (
            <div className={classes.upload}>
              <input
                id="knowledge-source-file"
                type="file"
                accept={accept}
                onChange={(event) => setForm((current) => ({ ...current, file: event.target.files?.[0] || null }))}
              />
              <Typography variant="caption" display="block" color="textSecondary">
                Máximo de {formatKnowledgeBytes(effectiveLimits.maxFileBytes)}. PDFs devem conter texto; OCR não faz parte deste MVP.
              </Typography>
              {limitError && <Typography variant="caption" display="block" color="error">{limitError}</Typography>}
            </div>
          )}
          {form.type === "text" && (
            <TextField label="Conteúdo" variant="outlined" multiline rows={10} value={form.text} onChange={update("text")} required inputProps={{ maxLength: effectiveLimits.maxTextCharacters }} helperText={limitError || `${form.text.length.toLocaleString("pt-BR")} de ${effectiveLimits.maxTextCharacters.toLocaleString("pt-BR")} caracteres`} error={Boolean(limitError)} />
          )}
          {form.type === "faq" && (
            <TextField
              label="Perguntas e respostas"
              variant="outlined"
              multiline
              rows={10}
              value={form.faqText}
              onChange={update("faqText")}
              required
              placeholder={"Qual é o horário? | Das 8h às 18h\nOnde vocês atendem? | Em todo o Brasil"}
              helperText={limitError || `${faqTextToItems(form.faqText).length} de ${effectiveLimits.maxFaqItems} perguntas e respostas, uma por linha, separadas por |`}
              error={!faqValid || Boolean(limitError)}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={save} disabled={!valid || saving}>
          {saving ? <CircularProgress size={22} /> : sourceId && form.type === "url" ? "Capturar nova revisão" : "Enviar para processamento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KnowledgeSourceModal;
